/**
 * Kitchen words for backend values. Presentation only: statuses and codes come from the backend; this
 * file decides how a cook reads them. Raw backend messages are never shown.
 */
import { isApiError } from '@/api/errors';
import type { BadgeTone } from '@/components/Badge';
import { formatLocation, isLocationType, venueVocabulary } from '@/venue/venue';
import type { LocationType } from '@/venue/venue';

/** "ORD-0042" → "#0042". Any other form is shown as the backend sent it. */
export function ticketNumber(orderNumber: string): string {
  const match = /^ORD-(\d+)$/.exec(orderNumber);
  return match ? `#${match[1]}` : orderNumber;
}

/** "Table 12" or "Seat B12". A location type this app does not know is shown as its bare label. */
export function locationText(type: string, label: string): string {
  return isLocationType(type) ? formatLocation(type, label) : label;
}

export function locationTypeOf(type: string): LocationType {
  return isLocationType(type) ? type : 'TABLE';
}

/** The backend status model, in the order the board shows it. No frontend-only states. */
export const BOARD_GROUPS = [
  { status: 'SUBMITTED', title: 'New' },
  { status: 'IN_PREPARATION', title: 'Preparing' },
  { status: 'READY', title: 'Ready' },
] as const;

export type BoardStatus = (typeof BOARD_GROUPS)[number]['status'];

type Tone = Exclude<BadgeTone, 'waiter'>;

const STATUS: Record<string, { label: string; tone: Tone }> = {
  SUBMITTED: { label: 'New', tone: 'neutral' },
  IN_PREPARATION: { label: 'Preparing', tone: 'primary' },
  READY: { label: 'Ready', tone: 'success' },
  SERVED: { label: 'Served', tone: 'neutral' },
};

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Label and tone for a ticket or item status. Never violet: that is reserved for "Added by waiter". */
export function statusView(status: string, locationType?: string): { label: string; tone: Tone } {
  const known = STATUS[status];
  if (!known) return { label: humanize(status), tone: 'neutral' };
  if (status === 'SERVED' && locationType) {
    return { ...known, label: venueVocabulary(locationTypeOf(locationType)).servedShort };
  }
  return known;
}

/** What a READY line is waiting for: a waiter at a table, any staff member at a cinema seat. */
export function waitingText(locationType: string): string {
  return locationTypeOf(locationType) === 'SEAT' ? 'Waiting for staff' : 'Waiting for a waiter';
}

/**
 * Which board tabs an order belongs on: every tab for which it has at least one line in that state.
 * Served lines belong to no tab (they are done); cancelled lines are not sent at all.
 */
export function linesIn<T extends { fulfillment_status: string }>(
  ticket: { items: T[] },
  status: BoardStatus,
): T[] {
  return ticket.items.filter((item) => item.fulfillment_status === status);
}

/**
 * The one step the kitchen can take next, as the backend allows it (09 §14.3). `null` when none: a READY
 * item waits for a waiter, who marks it served from the waiter screen (KWS-DEC-01).
 */
export function nextStep(status: string): { to: 'IN_PREPARATION' | 'READY'; label: string } | null {
  switch (status) {
    case 'SUBMITTED':
      return { to: 'IN_PREPARATION', label: 'Start' };
    case 'IN_PREPARATION':
      return { to: 'READY', label: 'Ready' };
    default:
      return null;
  }
}

// ─── Elapsed time (display only; the server supplies the figure) ─────────────

export function formatElapsed(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * How long a ticket has waited before it is drawn as late. These are presentation thresholds only: the
 * backend defines no service-time rule. They change colour, never behaviour.
 */
export const AGING_MINUTES = { attention: 10, late: 20 } as const;

export function agingOf(seconds: number): 'normal' | 'attention' | 'late' {
  const minutes = seconds / 60;
  if (minutes >= AGING_MINUTES.late) return 'late';
  if (minutes >= AGING_MINUTES.attention) return 'attention';
  return 'normal';
}

// ─── Assistance ──────────────────────────────────────────────────────────────

const ASSISTANCE: Record<string, string> = {
  CALL_WAITER: 'Needs a waiter',
  WATER: 'Water',
  CUTLERY: 'Cutlery',
  BILL: 'Wants the bill',
  OTHER: 'Other request',
};

export const assistanceLabel = (type: string): string => ASSISTANCE[type] ?? humanize(type);

// ─── Errors ──────────────────────────────────────────────────────────────────

/**
 * What went wrong, in words a cook can act on. Backend codes pick the sentence; the backend's own
 * message is never shown. Money and payment never appear, even when the backend's reason is financial.
 */
export function describeKitchenError(error: unknown): string {
  if (!isApiError(error)) return 'Something went wrong. Please try again.';
  if (error.isNetworkError) return "Couldn't reach the server. Check the connection and try again.";
  if (error.status === 401) return 'Your session has ended. Please sign in again.';
  if (error.hasCode('INVALID_TRANSITION'))
    return 'That item has already moved on. The board has been refreshed.';
  if (error.hasCode('ORDER_ITEM_NOT_FOUND') || error.hasCode('ORDER_NOT_FOUND'))
    return 'That order is no longer on the board. The board has been refreshed.';
  if (error.hasCode('SESSION_ALREADY_CLOSED')) return 'That order has already been closed.';
  if (error.hasCode('ITEM_ALREADY_SERVED'))
    return "That item was already served, so it can't be voided.";
  if (error.hasCode('BILL_BELOW_SETTLED'))
    return "This item can't be voided right now. Please check with the counter.";
  if (error.hasCode('ORDER_CANCELLED'))
    return 'That order was cancelled, so there is no ticket to print.';
  if (error.hasCode('NO_PRINTER_CONFIGURED'))
    return 'No kitchen printer is set up for this branch.';
  if (error.hasCode('PRINTER_NOT_FOUND')) return 'The kitchen printer could not be found.';
  if (error.hasCode('MENU_ITEM_NOT_FOUND'))
    return 'That dish is no longer on the menu. The list has been refreshed.';
  if (error.hasCode('ASSISTANCE_NOT_FOUND'))
    return 'That request is no longer open. The list has been refreshed.';
  if (error.hasCode('DUPLICATE_IDEMPOTENCY_KEY'))
    return 'That print request was already used. Please try again.';
  if (error.status === 403) return "You're not allowed to do that.";
  if (error.status === 400 || error.status === 422)
    return 'Please check what you entered and try again.';
  if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return "That didn't go through. Please try again.";
}

/** One sentence for a finished reprint, from the print job's own status. `ok` is false for a failure. */
export function describeReprint(status: string): { ok: boolean; message: string } {
  switch (status) {
    case 'SENT':
      return { ok: true, message: 'Ticket sent to the kitchen printer.' };
    case 'PENDING':
      return { ok: true, message: 'Reprint queued. It has not printed yet.' };
    case 'FAILED':
      return {
        ok: false,
        message: 'The ticket did not print. Check the kitchen printer, then try again.',
      };
    default:
      return { ok: false, message: 'The reprint status is unknown. Check the printer.' };
  }
}
